import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ChangeEvent,
  type DragEvent,
} from "react";
import { createPortal } from "react-dom";
import { useNavigate } from "react-router-dom";
import {
  ArrowLeft,
  Folder,
  Info,
  LayoutGrid,
  ImageOff,
  Loader2,
  Plus,
  Heart,
  Search,
  X,
} from "lucide-react";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { Input } from "@/components/ui/input";
import {
  countHubStyles,
  DEFAULT_HUB_STYLES_FILTERS,
  filterHubCollections,
  type HubStylesFilterState,
} from "@/features/footwear-studio/HubStylesList";
import { FilterDropdown } from "@/features/footwear-studio/FilterDropdown";
import {
  HubStylesPanelList,
  displayCollectionName,
  invokeHubStylePrefetch,
} from "@/features/footwear-studio/HubStylesPanel";
import { excludeHiddenStyles } from "@/features/footwear-studio/homeFolders";
import { OverlayFlowChain } from "@/features/footwear-studio/OverlayFlowChain";
import type { OverlayOpenStageHandler } from "@/features/footwear-studio/OverlayFlowChain";
import {
  QuietActionButton,
  StudioBestResultsPanel,
  StudioUploadDropzone,
} from "@/features/footwear-studio/StudioUploadPanels";
import { cn } from "@/lib/utils";
import { useRegisterStudioChainOpen, useStudioUpload } from "./StudioUploadActions";
import {
  SELECT_FEEDBACK_CEILING_MS,
  type SelectFeedbackPhase,
} from "./studioRailSelectFeedback";

const FADE_MS = 150;

type OverlayView = "upload" | "designs" | "chain";

export function StudioRailUploadButton({
  inlineIcon = false,
  showTooltip = true,
}: {
  inlineIcon?: boolean;
  showTooltip?: boolean;
} = {}) {
  const {
    onFileUpload,
    hasSource,
    overlayOpen,
    setOverlayOpen,
    triggerFilePicker,
    hubStyles,
    activeStyleId,
    onSelectStyle,
    versionHistory,
    versionsLoading,
    activeMasterJobId,
    pinnedJobIds,
    onSelectVersion,
    onTogglePin,
    onOpenStage,
  } = useStudioUpload();
  const navigate = useNavigate();
  const open = overlayOpen;
  const setOpen = setOverlayOpen;
  /** Keep portal mounted during exit fade. */
  const [mounted, setMounted] = useState(false);
  /** Drives opacity 0↔1 for the fade. */
  const [visible, setVisible] = useState(false);
  const [reduceMotion, setReduceMotion] = useState(false);
  const buttonRef = useRef<HTMLButtonElement>(null);
  const popoverRef = useRef<HTMLDivElement>(null);
  /** True while the native file dialog is open — ignore outside-click / Escape closes. */
  const pickingFileRef = useRef(false);
  const fadeTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const [overlayView, setOverlayView] = useState<OverlayView>("upload");
  const [hubPinFilter, setHubPinFilter] = useState<"all" | "pinned">("all");
  const [hubFilters, setHubFilters] =
    useState<HubStylesFilterState>(DEFAULT_HUB_STYLES_FILTERS);
  /** pending = Loader2; failed = ImageOff terminal (§7 #5). */
  const [selectFeedbackPhase, setSelectFeedbackPhase] =
    useState<SelectFeedbackPhase>("idle");
  const [selectFeedbackStyleId, setSelectFeedbackStyleId] = useState<
    string | null
  >(null);
  const [chainStyleId, setChainStyleId] = useState<string | null>(null);
  const hoverDebounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const selectFeedbackCeilingRef = useRef<ReturnType<typeof setTimeout> | null>(
    null,
  );
  const selectFeedbackGenRef = useRef(0);

  const handleHoverStyle = useCallback((styleId: string) => {
    if (hoverDebounceRef.current) clearTimeout(hoverDebounceRef.current);
    hoverDebounceRef.current = setTimeout(() => {
      hoverDebounceRef.current = null;
      invokeHubStylePrefetch(styleId);
    }, 200);
  }, []);

  useEffect(() => {
    return () => {
      if (hoverDebounceRef.current) clearTimeout(hoverDebounceRef.current);
    };
  }, []);

  useRegisterStudioChainOpen(overlayView === "chain" && !!chainStyleId);

  const listCollections = useMemo(
    () => excludeHiddenStyles(hubStyles),
    [hubStyles],
  );

  const chainStyleCard = useMemo(() => {
    if (!chainStyleId) return null;
    for (const collection of hubStyles) {
      const match = collection.styles.find((s) => s.id === chainStyleId);
      if (match) return match;
    }
    return null;
  }, [hubStyles, chainStyleId]);

  const hasDesigns = useMemo(
    () =>
      countHubStyles(listCollections).styles > 0 &&
      typeof onSelectStyle === "function",
    [listCollections, onSelectStyle],
  );

  const visibleHubCollections = useMemo(() => {
    let cols = filterHubCollections(listCollections, hubFilters);
    if (hubPinFilter === "pinned") {
      cols = cols
        .map((c) => ({
          ...c,
          styles: c.styles.filter((s) => !!s.has_favourite),
        }))
        .filter((c) => c.styles.length > 0);
    }
    return cols;
  }, [listCollections, hubFilters, hubPinFilter]);

  const designsEmptyMessage = useMemo(() => {
    if (listCollections.every((c) => c.styles.length === 0)) return "No styles yet";
    if (hubPinFilter === "pinned") return "No favourites yet";
    return "No styles match";
  }, [listCollections, hubPinFilter]);

  const collectionFilterOptions = useMemo(() => {
    const options = [{ value: "all", label: "All" }];
    for (const collection of listCollections) {
      options.push({
        value: collection.id,
        label: displayCollectionName(collection),
      });
    }
    return options;
  }, [listCollections]);

  const typeFilterOptions = useMemo(() => {
    const types = new Set<string>();
    for (const collection of listCollections) {
      for (const style of collection.styles) {
        const shoeType = style.shoe_type?.trim();
        if (shoeType) types.add(shoeType);
      }
    }
    return [
      { value: "all", label: "All" },
      ...[...types].sort().map((type) => ({ value: type, label: type })),
    ];
  }, [listCollections]);

  const clearSelectFeedback = useCallback(() => {
    selectFeedbackGenRef.current += 1;
    if (selectFeedbackCeilingRef.current) {
      clearTimeout(selectFeedbackCeilingRef.current);
      selectFeedbackCeilingRef.current = null;
    }
    setSelectFeedbackPhase("idle");
    setSelectFeedbackStyleId(null);
  }, []);

  useEffect(() => {
    if (open) {
      setOverlayView("upload");
      setHubPinFilter("all");
      setHubFilters(DEFAULT_HUB_STYLES_FILTERS);
      clearSelectFeedback();
      setChainStyleId(null);
    }
  }, [open, clearSelectFeedback]);

  useEffect(() => {
    if (hasSource) {
      clearSelectFeedback();
    }
  }, [hasSource, clearSelectFeedback]);

  useEffect(() => {
    if (!hasDesigns && (overlayView === "designs" || overlayView === "chain")) {
      setOverlayView("upload");
      setChainStyleId(null);
    }
  }, [hasDesigns, overlayView]);

  useEffect(() => {
    return () => {
      if (selectFeedbackCeilingRef.current) {
        clearTimeout(selectFeedbackCeilingRef.current);
      }
    };
  }, []);

  const handleDesignSelect = useCallback(
    (styleId: string) => {
      const gen = ++selectFeedbackGenRef.current;
      setSelectFeedbackPhase("pending");
      setSelectFeedbackStyleId(styleId);

      if (selectFeedbackCeilingRef.current) {
        clearTimeout(selectFeedbackCeilingRef.current);
      }

      // Ceiling → terminal failed (ImageOff), not a silent clear (§7 #5).
      selectFeedbackCeilingRef.current = setTimeout(() => {
        selectFeedbackCeilingRef.current = null;
        if (selectFeedbackGenRef.current !== gen) return;
        setSelectFeedbackPhase("failed");
        setSelectFeedbackStyleId(styleId);
      }, SELECT_FEEDBACK_CEILING_MS);

      void Promise.resolve(onSelectStyle?.(styleId)).then(
        () => {
          if (selectFeedbackGenRef.current !== gen) return;
          if (selectFeedbackCeilingRef.current) {
            clearTimeout(selectFeedbackCeilingRef.current);
            selectFeedbackCeilingRef.current = null;
          }
          setSelectFeedbackPhase("idle");
          setSelectFeedbackStyleId(null);
        },
        () => {
          if (selectFeedbackGenRef.current !== gen) return;
          if (selectFeedbackCeilingRef.current) {
            clearTimeout(selectFeedbackCeilingRef.current);
            selectFeedbackCeilingRef.current = null;
          }
          setSelectFeedbackPhase("failed");
          setSelectFeedbackStyleId(styleId);
        },
      );
    },
    [onSelectStyle],
  );

  const handleDesignOpen = useCallback(
    (styleId: string) => {
      handleDesignSelect(styleId);
      setChainStyleId(styleId);
      setOverlayView("chain");
    },
    [handleDesignSelect],
  );

  const handleOpenStageFromChain = useCallback<OverlayOpenStageHandler>(
    async (styleId, stage, context) => {
      if (!onOpenStage) return false;
      const ok = await onOpenStage(styleId, stage, context);
      if (ok !== false) {
        setChainStyleId(null);
        setOverlayView("designs");
      }
      return ok;
    },
    [onOpenStage],
  );

  useEffect(() => {
    const mq = window.matchMedia("(prefers-reduced-motion: reduce)");
    setReduceMotion(mq.matches);
    const onChange = () => setReduceMotion(mq.matches);
    mq.addEventListener("change", onChange);
    return () => mq.removeEventListener("change", onChange);
  }, []);

  // Enter / exit fade (respect reduced motion → instant)
  useEffect(() => {
    if (fadeTimerRef.current) {
      clearTimeout(fadeTimerRef.current);
      fadeTimerRef.current = null;
    }

    if (open) {
      setMounted(true);
      if (reduceMotion) {
        setVisible(true);
        return;
      }
      setVisible(false);
      const id = requestAnimationFrame(() => {
        requestAnimationFrame(() => setVisible(true));
      });
      return () => cancelAnimationFrame(id);
    }

    // Closing — fade out, then unmount
    setVisible(false);
    if (reduceMotion) {
      setMounted(false);
      return;
    }
    fadeTimerRef.current = setTimeout(() => {
      setMounted(false);
      fadeTimerRef.current = null;
    }, FADE_MS);
    return () => {
      if (fadeTimerRef.current) {
        clearTimeout(fadeTimerRef.current);
        fadeTimerRef.current = null;
      }
    };
  }, [open, reduceMotion]);

  useEffect(() => {
    if (!open) return;

    const handleOutside = (event: MouseEvent | TouchEvent) => {
      if (!hasSource) return;
      // Native file dialog can steal focus / synthesize events — keep overlay open.
      if (pickingFileRef.current) return;
      const target = event.target as Node;
      if (
        buttonRef.current?.contains(target) ||
        popoverRef.current?.contains(target)
      ) {
        return;
      }
      setOpen(false);
    };

    const handleEscape = (event: KeyboardEvent) => {
      if (!hasSource) return;
      if (pickingFileRef.current) return;
      if (event.key === "Escape") setOpen(false);
    };

    // After OS picker closes (select or cancel), focus returns to the window.
    const handleWindowFocus = () => {
      window.setTimeout(() => {
        pickingFileRef.current = false;
      }, 300);
    };

    document.addEventListener("mousedown", handleOutside, true);
    document.addEventListener("touchstart", handleOutside, true);
    window.addEventListener("keydown", handleEscape);
    window.addEventListener("focus", handleWindowFocus);
    return () => {
      document.removeEventListener("mousedown", handleOutside, true);
      document.removeEventListener("touchstart", handleOutside, true);
      window.removeEventListener("keydown", handleEscape);
      window.removeEventListener("focus", handleWindowFocus);
    };
  }, [open, hasSource, setOpen]);

  const handleFocusTrapKeyDown = useCallback(
    (event: KeyboardEvent) => {
      if (!open) return;
      if (event.key === "Tab" && popoverRef.current) {
        const focusable = popoverRef.current.querySelectorAll<HTMLElement>(
          'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])',
        );
        if (focusable.length === 0) return;
        const first = focusable[0];
        const last = focusable[focusable.length - 1];
        if (event.shiftKey && document.activeElement === first) {
          event.preventDefault();
          last.focus();
        } else if (!event.shiftKey && document.activeElement === last) {
          event.preventDefault();
          first.focus();
        }
      }
    },
    [open],
  );

  useEffect(() => {
    if (!open) return;
    document.addEventListener("keydown", handleFocusTrapKeyDown);
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    const focusable = popoverRef.current?.querySelectorAll<HTMLElement>(
      'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])',
    );
    focusable?.[0]?.focus();
    return () => {
      document.removeEventListener("keydown", handleFocusTrapKeyDown);
      document.body.style.overflow = prevOverflow;
    };
  }, [open, handleFocusTrapKeyDown]);

  const handleOverlayFileUpload = useCallback(
    (e: ChangeEvent<HTMLInputElement>) => {
      if (e.target.files?.length && onFileUpload) {
        onFileUpload(e);
        setOpen(false);
      }
    },
    [onFileUpload, setOpen],
  );

  const handleModalDragOver = (event: DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    event.stopPropagation();
  };

  const handleModalDrop = (event: DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    event.stopPropagation();
  };

  const handleClose = () => {
    if (!hasSource) return;
    if (pickingFileRef.current) return;
    setOpen(false);
  };

  const handleBackToUpload = () => {
    setOverlayView("upload");
    setChainStyleId(null);
    clearSelectFeedback();
  };

  const handleModalBack = () => {
    if (overlayView === "chain") {
      setOverlayView("designs");
      setChainStyleId(null);
      return;
    }
    handleBackToUpload();
  };

  const uploadTrigger = (
    <button
      ref={buttonRef}
      type="button"
      onClick={() => {
        if (!hasSource) {
          triggerFilePicker();
          return;
        }
        setOpen((v) => !v);
      }}
      className={cn(
        "flex items-center justify-center transition-colors duration-[160ms] cursor-pointer",
        inlineIcon
          ? "size-5 shrink-0 p-0 text-sky-400/90 hover:text-sky-300"
          : "h-14 w-14 text-sky-400/90 hover:bg-white/[0.06] hover:text-sky-300",
      )}
      aria-label="Upload"
      aria-expanded={open}
      data-testid="studio-rail-upload-trigger"
    >
      <Plus className="size-5" strokeWidth={2} />
    </button>
  );

  return (
    <>
      {showTooltip ? (
        <Tooltip>
          <TooltipTrigger asChild>{uploadTrigger}</TooltipTrigger>
          <TooltipContent
            side="right"
            sideOffset={8}
            className="border-neutral-700 bg-bgSurface"
          >
            Upload
          </TooltipContent>
        </Tooltip>
      ) : (
        uploadTrigger
      )}

      {mounted &&
        createPortal(
          <>
            <div
              className="fixed inset-0 z-40 bg-black/50 backdrop-blur-sm"
              aria-hidden
              onClick={handleClose}
              style={{
                opacity: visible ? 1 : 0,
                transition: reduceMotion ? "none" : `opacity ${FADE_MS}ms ease`,
                pointerEvents: visible ? "auto" : "none",
              }}
            />
            <div
              ref={popoverRef}
              role="dialog"
              aria-modal="true"
              aria-label="Start a design"
              className="fixed left-1/2 top-1/2 z-50 -translate-x-1/2 -translate-y-1/2 flex flex-col overflow-hidden rounded-[14px] border border-white/[0.08] shadow-[0_16px_48px_rgba(0,0,0,0.55)]"
              style={{
                width: "min(900px, calc(100vw - 2rem))",
                ...(overlayView === "upload"
                  ? {
                      height: "auto",
                      maxHeight: "calc(100vh - 4rem)",
                    }
                  : {
                      height: "min(600px, calc(100vh - 4rem))",
                    }),
                background:
                  "linear-gradient(180deg, rgba(255,255,255,0.035) 0%, transparent 55%), radial-gradient(ellipse 70% 60% at 50% 45%, rgba(56,189,248,0.04) 0%, transparent 70%), var(--bg-surface)",
                opacity: visible ? 1 : 0,
                transition: reduceMotion ? "none" : `opacity ${FADE_MS}ms ease`,
                pointerEvents: visible ? "auto" : "none",
              }}
              data-testid="studio-rail-upload-popover"
              onDragOver={handleModalDragOver}
              onDrop={handleModalDrop}
            >
            {overlayView !== "chain" && (
            <div className="shrink-0 flex items-center gap-2 px-4 py-3 border-b border-white/[0.06] min-w-0">
              {overlayView === "designs" && (
                <button
                  type="button"
                  onClick={handleModalBack}
                  className="flex items-center justify-center rounded-md text-white/45 hover:text-white/75 transition-colors shrink-0"
                  aria-label="Back"
                  data-testid="studio-rail-upload-back"
                >
                  <ArrowLeft className="h-4 w-4" strokeWidth={2} />
                </button>
              )}
              {overlayView === "designs" ? (
                <div className="flex flex-1 items-center justify-between gap-4 min-w-0 overflow-hidden">
                  <div className="min-w-0 shrink mr-1">
                    <p className="text-[15px] font-medium text-white/[0.92] truncate">
                      Choose a saved design
                    </p>
                    <p
                      className="flex items-center gap-1 truncate leading-tight mt-0.5 min-w-0"
                      style={{ fontSize: 12.5, color: "rgba(255,255,255,0.5)" }}
                    >
                      <span className="truncate">Click any design to open it.</span>
                      <Info
                        className="h-[14px] w-[14px] shrink-0"
                        style={{ color: "rgba(255,255,255,0.5)" }}
                        aria-hidden
                      />
                    </p>
                  </div>
                  <div className="flex items-center gap-2.5 shrink-0 mr-2">
                    <div
                      className="inline-flex shrink-0 rounded-full p-0.5"
                      style={{ border: "1px solid rgba(255,255,255,0.10)" }}
                      role="group"
                      aria-label="Favourites filter"
                    >
                      <button
                        type="button"
                        onClick={() => setHubPinFilter("all")}
                        className={cn(
                          "rounded-full px-2.5 py-1 text-[12px] font-medium transition-colors",
                          hubPinFilter === "all"
                            ? "bg-[rgba(56,189,248,0.16)] text-[#5cc8f5]"
                            : "text-white/45 hover:text-white/65",
                        )}
                      >
                        All
                      </button>
                      <button
                        type="button"
                        onClick={() => setHubPinFilter("pinned")}
                        className={cn(
                          "inline-flex items-center gap-1 rounded-full px-2.5 py-1 text-[12px] font-medium transition-colors",
                          hubPinFilter === "pinned"
                            ? "bg-[rgba(56,189,248,0.16)] text-[#5cc8f5]"
                            : "text-white/45 hover:text-white/65",
                        )}
                      >
                        <Heart
                          className="h-3 w-3"
                          fill={
                            hubPinFilter === "pinned" ? "currentColor" : "none"
                          }
                        />
                        Favourites
                      </button>
                    </div>

                    <FilterDropdown
                      label="Collection"
                      value={hubFilters.collectionFilter}
                      onChange={(value) =>
                        setHubFilters((prev) => ({
                          ...prev,
                          collectionFilter: value,
                        }))
                      }
                      options={collectionFilterOptions}
                      testId="studio-rail-upload-filter-collection"
                    />
                    <FilterDropdown
                      label="Shoe Type"
                      value={hubFilters.typeFilter}
                      onChange={(value) =>
                        setHubFilters((prev) => ({
                          ...prev,
                          typeFilter: value,
                        }))
                      }
                      options={typeFilterOptions}
                      testId="studio-rail-upload-filter-type"
                    />

                    <div className="relative w-[185px] shrink-0">
                      <Search className="pointer-events-none absolute left-2.5 top-1/2 z-10 h-3.5 w-3.5 -translate-y-1/2 text-white/35 shrink-0" />
                      <Input
                        placeholder="Search designs..."
                        value={hubFilters.search}
                        onChange={(e) =>
                          setHubFilters((prev) => ({
                            ...prev,
                            search: e.target.value,
                          }))
                        }
                        className="h-8 w-[148px] max-w-full pl-8 text-[12px] md:text-[12px] font-medium bg-bgNested border-white/10 text-white/80 placeholder:text-white/40 transition-[width] duration-200 ease-out focus-visible:w-[185px] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#38bdf8]/55 focus-visible:ring-offset-0 focus-visible:border-[#38bdf8]/55"
                      />
                    </div>
                  </div>
                </div>
              ) : (
                <>
                  <div className="min-w-0">
                    <p className="text-[15px] font-medium text-white/[0.92] truncate">
                      Start a new design
                    </p>
                    <p
                      className="truncate leading-tight mt-0.5"
                      style={{ fontSize: 12.5, color: "rgba(255,255,255,0.5)" }}
                    >
                      Upload one clear side-view image of a single shoe.
                    </p>
                  </div>
                  <div className="flex-1" />
                </>
              )}
              {overlayView === "designs" && selectFeedbackPhase === "pending" ? (
                <Loader2
                  className="h-4 w-4 animate-spin text-sky-400/80 shrink-0"
                  aria-label="Loading design"
                />
              ) : null}
              {overlayView === "designs" && selectFeedbackPhase === "failed" ? (
                <ImageOff
                  className="h-4 w-4 text-white/45 shrink-0"
                  aria-label="Failed to open design"
                  data-testid="rail-select-feedback-failed"
                />
              ) : null}
              {hasSource && (
                <button
                  type="button"
                  onClick={handleClose}
                  className="flex items-center justify-center w-8 h-8 rounded-full text-white/50 hover:text-white hover:bg-white/[0.07] transition-colors shrink-0"
                  aria-label="Close"
                >
                  <X className="h-[18px] w-[18px]" />
                </button>
              )}
            </div>
            )}

            <div
              className={cn(
                "flex flex-col min-h-0 overflow-hidden",
                overlayView !== "upload" && "flex-1",
              )}
              style={{
                padding: 0,
              }}
            >
              {overlayView === "upload" ? (
                <div className="flex flex-col min-h-0 overflow-hidden">
                  <div className="flex gap-[16px]" style={{ padding: "16px 20px" }}>
                    <StudioUploadDropzone
                      onFileUpload={onFileUpload ? handleOverlayFileUpload : null}
                      testId="studio-rail-upload-drop-zone"
                    />
                    <StudioBestResultsPanel />
                  </div>

                  <div
                    className="flex items-center gap-3 px-5"
                    style={{ paddingTop: 3, paddingBottom: 3 }}
                  >
                    <div
                      className="flex-1"
                      style={{ height: 1, background: "rgba(255,255,255,0.07)" }}
                    />
                    <span style={{ fontSize: 11.5, color: "rgba(255,255,255,0.38)" }}>
                      {hasDesigns
                        ? "Or continue from a saved design"
                        : "No saved designs yet"}
                    </span>
                    <div
                      className="flex-1"
                      style={{ height: 1, background: "rgba(255,255,255,0.07)" }}
                    />
                  </div>

                  <div
                    className="flex flex-col items-center"
                    style={{ padding: "12px 20px 15px", gap: 6 }}
                  >
                    {hasDesigns ? (
                      <>
                        <QuietActionButton
                          label="Choose a saved design"
                          icon={LayoutGrid}
                          onClick={() => {
                            setOpen(false);
                            navigate("/footwear-studio/library");
                          }}
                          testId="studio-rail-upload-btn-from-designs"
                        />
                        <p
                          className="text-center"
                          style={{
                            fontSize: 11.5,
                            color: "rgba(255,255,255,0.38)",
                          }}
                        >
                          Open an existing design to pick up where you left off.
                        </p>
                      </>
                    ) : (
                      <div
                        className="w-full flex items-center gap-3.5"
                        style={{
                          border: "1px dashed rgba(255,255,255,0.1)",
                          borderRadius: 11,
                          padding: 18,
                        }}
                      >
                        <div
                          className="flex items-center justify-center shrink-0 rounded-full"
                          style={{
                            width: 46,
                            height: 46,
                            border: "1px solid rgba(255,255,255,0.1)",
                          }}
                        >
                          <Folder
                            style={{
                              width: 20,
                              height: 20,
                              color: "rgba(255,255,255,0.28)",
                            }}
                            strokeWidth={1.75}
                          />
                        </div>
                        <div className="min-w-0 text-left">
                          <p
                            style={{
                              fontSize: 13.5,
                              color: "rgba(255,255,255,0.85)",
                              lineHeight: 1.35,
                            }}
                          >
                            Your first upload will create a design.
                          </p>
                          <p
                            style={{
                              fontSize: 12,
                              color: "rgba(255,255,255,0.42)",
                              lineHeight: 1.4,
                              marginTop: 2,
                            }}
                          >
                            Saved designs will appear here once you have them.
                          </p>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              ) : overlayView === "chain" && chainStyleId ? (
                <div className="flex flex-1 flex-col min-h-0 relative">
                  <OverlayFlowChain
                    styleId={chainStyleId}
                    styleCard={chainStyleCard}
                    versionHistory={versionHistory}
                    versionsLoading={versionsLoading}
                    activeMasterJobId={activeMasterJobId}
                    pinnedJobIds={pinnedJobIds}
                    onBack={() => {
                      setOverlayView("designs");
                      setChainStyleId(null);
                    }}
                    onSelectVersion={onSelectVersion ?? undefined}
                    onTogglePin={onTogglePin ?? undefined}
                    onOpenStage={onOpenStage ? handleOpenStageFromChain : undefined}
                    onClose={handleClose}
                    showClose={hasSource}
                    loadingDesign={
                      selectFeedbackPhase === "pending" &&
                      selectFeedbackStyleId === chainStyleId
                    }
                    designLoadFailed={
                      selectFeedbackPhase === "failed" &&
                      selectFeedbackStyleId === chainStyleId
                    }
                  />
                </div>
              ) : (
                <div className="flex flex-1 flex-col w-full min-w-0 min-h-0">
                  <div className="flex-1 min-h-0 overflow-y-auto stronom-scroll">
                    <HubStylesPanelList
                      collections={visibleHubCollections}
                      activeStyleId={activeStyleId}
                      onSelectStyle={handleDesignOpen}
                      onHoverStyle={handleHoverStyle}
                      emptyMessage={designsEmptyMessage}
                      rowLayout="overlay"
                    />
                  </div>
                </div>
              )}
            </div>
            </div>
          </>,
          document.body,
        )}
    </>
  );
}
